import React, { Component } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import AwesomeDebouncePromise from 'awesome-debounce-promise';
import classnames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/pro-light-svg-icons';

import Loader from '../../General/Loader';
import ProductSearchResults from './ProductSearchResults';

import { getProducts } from '../../../api/products';

import './ProductsSearchView.scss';
import { getBrandNameWithFallback } from '../form/lib/brandHelpers';

const TEST_SEARCH_TERM = '';

// Empty restores the "Best in X" view and digit-only is a product-ID lookup — neither hits Algolia.
const isSearchableQuery = val => val.trim() === '' || val.trim().length >= 3 || /^\d+$/.test(val.trim());

class ProductsSearchView extends Component {
  static propTypes = {
    catalog: PropTypes.object.isRequired,
    selectProduct: PropTypes.func.isRequired,
    matchSelectedTicket: PropTypes.func.isRequired,
    setSearchRefresh: PropTypes.func.isRequired,
    activeFilters: PropTypes.shape({
      industry: PropTypes.object,
      department: PropTypes.object,
      category: PropTypes.object,
      tag: PropTypes.object,
      AllBrand_id: PropTypes.number
    }),
    inMatchingMode: PropTypes.bool.isRequired,
    selectedTicket: PropTypes.object,
    selectedProduct: PropTypes.object,
    onProductUpdate: PropTypes.func
  };
  componentDidMount() {
    this.configureSearchDebouncer();
    TEST_SEARCH_TERM && setTimeout(() => this.changeSearch(TEST_SEARCH_TERM), 200);
    this.performSearch();
  }

  componentDidUpdate(prevProps) {
    if (!_.isEqual(prevProps.activeFilters, this.props.activeFilters)) {
      this.performSearch();
    }
  }

  state = {
    searchVal: '',
    isSearching: false,
    visibleProducts: []
  };

  latestSearchId = 0;

  performSearch = async ({ forceFresh = false } = {}) => {
    // Debounced calls read state at fire time, so re-check in case the query shrank below the gate;
    // bumping the id discards any in-flight search so its results can't land under the shorter input
    if (!isSearchableQuery(this.state.searchVal)) {
      this.latestSearchId++;
      return this.setState({ isSearching: false, visibleProducts: [] });
    }
    const { activeFilters } = this.props;
    const { category, department, industry, tag, AllBrand_id } = activeFilters;
    const filters = _.reduce(
      {
        Industry_id: _.get(industry, 'id'),
        Department_id: _.get(department, 'id'),
        Category_id: _.get(category, 'id'),
        AllBrand_id,
        Tag_id: _.get(tag, 'id'),
        query: this.state.searchVal,
        pageSize: 200
      },
      (res, v, k) => (v ? { ...res, [k]: v } : res),
      {}
    );
    const searchId = ++this.latestSearchId;
    this.setState({ isSearching: true });
    try {
      const { products } = await getProducts(filters, { forceFresh });
      if (searchId !== this.latestSearchId) return;
      this.setState({
        visibleProducts: _.orderBy(products, ['totalRank', 'industryRank', 'departmentRank', 'categoryRank']),
        isSearching: false
      });
    } catch {
      if (searchId !== this.latestSearchId) return;
      this.setState({ isSearching: false });
      window.ALERT.error('Failed to search products. Please try again.');
    }
  };

  configureSearchDebouncer = () => {
    this.searchProductsDebounced = AwesomeDebouncePromise(this.performSearch, 250);
    this.props.setSearchRefresh(newVal => {
      this.setState({ isSearching: true, searchVal: _.isNil(newVal) ? this.state.searchVal : newVal });
      setTimeout(
        () => {
          // Bare refreshes follow a product mutation and must bypass the search caches
          this.performSearch(newVal ? {} : { forceFresh: true });
        },
        newVal ? 0 : this.state.searchVal.length ? 1250 : 250
      ); // Due to asynchronous algolia writes
    });
  };

  changeSearch = newVal => {
    if (!isSearchableQuery(newVal)) {
      this.latestSearchId++;
      this.setState({ searchVal: newVal, isSearching: false, visibleProducts: [] });
      return;
    }
    this.setState({ searchVal: newVal, isSearching: true });
    this.searchProductsDebounced();
  };

  getResultsHeader = () => {
    const { activeFilters } = this.props;
    const { department, category, tag, AllBrand_id, industry } = activeFilters;
    const { searchVal } = this.state;
    const brand = AllBrand_id && this.props.catalog.brands.find(b => b.id === AllBrand_id);
    if (searchVal) return null;
    if (tag) return `Best in ${category.name} | ${tag.name}`;
    if (brand) return category ? `Best in ${category.name} | ${getBrandNameWithFallback(brand)}` : `Best in ${getBrandNameWithFallback(brand)}`;
    if (category) return `Best in ${category.name}`;
    if (department) return `Best in ${department.name}`;
    if (industry) return `Best in ${industry.name}`;
    return 'Best of Everything';
  };

  render() {
    const { catalog } = this.props;
    const { searchVal, visibleProducts, isSearching } = this.state;
    const header = this.getResultsHeader();
    return (
      <div className='products-search-view-outer-container panel'>
        <div className='search-input-container'>
          <input
            className='search-input'
            placeholder={header ? `Search ${header}` : 'Search by title or product ID'}
            value={searchVal}
            onChange={({ target }) => this.changeSearch(target.value)}
          />
          {!!searchVal.length && (
            <div className='search-clear-btn' onClick={() => this.changeSearch('')}>
              <FontAwesomeIcon icon={faTimes}></FontAwesomeIcon>
            </div>
          )}
        </div>
        <div className={classnames('products-search-view-container', { searching: isSearching })}>
          {searchVal.length > 0 || visibleProducts.length ? (
            <ProductSearchResults
              catalog={catalog}
              results={visibleProducts}
              inMatchingMode={this.props.inMatchingMode}
              selectedTicket={this.props.selectedTicket}
              activeFilters={this.props.activeFilters}
              selectedProduct={this.props.selectedProduct}
              isSearching={isSearching}
              selectProduct={this.props.selectProduct}
              matchSelectedTicket={this.props.matchSelectedTicket}
              onProductUpdate={this.props.onProductUpdate}
            />
          ) : (
            <div>{/* Hierarchy goes here. */}</div>
          )}
          <div className={classnames('searching-overlay', { hidden: !isSearching })}>
            <Loader />
          </div>
        </div>
      </div>
    );
  }
}

export default ProductsSearchView;
